// Artwork Status reference data.
// Mirrors the ArtworkStatus lookup table (ArtworkStatusId / Name / Code / Category / SortOrder / IsSystem / ExtendedInfoType).
// Category groupings (for color-coding only — not necessarily the same "Category" column values,
// though they were used as the starting point):
//   sale      -> Available, Hold, Sold, Not For Sale, Auction
//   movement  -> Loan Out, Loan In, Consignment, Exhibition, Transit
//   custody   -> In Collection, With Restorer, In Storage
//   alert     -> Missing, Lost, Stolen, Damaged, Destroyed
//   external  -> With Brokerage, With Gallery/Auction House

import SellIcon from "@mui/icons-material/Sell";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import GavelIcon from "@mui/icons-material/Gavel";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import HandshakeIcon from "@mui/icons-material/Handshake";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import MuseumIcon from "@mui/icons-material/Museum";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HandymanIcon from "@mui/icons-material/Handyman";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import StorefrontIcon from "@mui/icons-material/Storefront";
import type { SvgIconComponent } from "@mui/icons-material";

export type ArtworkStatusExtendedInfoType = "NONE" | "VALUE" | "AUCT" | "OUT" | "FROM";

// Colors reference the badge palette (see ArtworkStatusBadge). Swap these for theme tokens if preferred.
export type ArtworkStatusColor = "success" | "warning" | "default" | "info" | "secondary" | "error";

export interface ArtworkStatusInfo {
    artworkStatusId: number;
    name: string;
    code: string;
    category: number;
    sortOrder: number;
    isSystem: boolean;
    extendedInfoType: ArtworkStatusExtendedInfoType;
    color: ArtworkStatusColor;
    icon: SvgIconComponent;
}

// Human-readable group label for each status "category" number — used to group
// the status list in filter UIs (DrawerSearch, QuickFilterMenu).
export const ARTWORK_STATUS_CATEGORY_LABELS: Record<number, string> = {
    1: "Sale",
    2: "Loan & Consignment",
    3: "Location & Logistics",
    4: "Alerts",
    5: "External Custody",
};

export const ARTWORK_STATUSES: ArtworkStatusInfo[] = [
    { artworkStatusId: 1, name: "Available For Sale", code: "AVAIL", category: 1, sortOrder: 10, isSystem: true, extendedInfoType: "NONE", color: "success", icon: SellIcon },
    { artworkStatusId: 2, name: "Reserved / On Hold", code: "HOLD", category: 1, sortOrder: 20, isSystem: true, extendedInfoType: "NONE", color: "warning", icon: PauseCircleOutlinedIcon },
    { artworkStatusId: 3, name: "Sold", code: "SOLD", category: 1, sortOrder: 30, isSystem: true, extendedInfoType: "VALUE", color: "default", icon: CheckCircleIcon },
    { artworkStatusId: 4, name: "Not For Sale", code: "NFS", category: 1, sortOrder: 40, isSystem: true, extendedInfoType: "NONE", color: "default", icon: BlockIcon },
    { artworkStatusId: 5, name: "Consigned for Auction", code: "AUCT", category: 1, sortOrder: 50, isSystem: true, extendedInfoType: "AUCT", color: "secondary", icon: GavelIcon },

    { artworkStatusId: 10, name: "On Loan (Outgoing)", code: "LOAN_OUT", category: 2, sortOrder: 10, isSystem: true, extendedInfoType: "OUT", color: "info", icon: LogoutIcon },
    { artworkStatusId: 11, name: "On Loan (Incoming)", code: "LOAN_IN", category: 2, sortOrder: 20, isSystem: true, extendedInfoType: "FROM", color: "info", icon: LoginIcon },
    { artworkStatusId: 12, name: "On Consignment", code: "OCONS", category: 2, sortOrder: 30, isSystem: true, extendedInfoType: "NONE", color: "secondary", icon: HandshakeIcon },

    { artworkStatusId: 20, name: "In Collection", code: "COLL", category: 3, sortOrder: 10, isSystem: true, extendedInfoType: "NONE", color: "default", icon: Inventory2Icon },
    { artworkStatusId: 21, name: "On Display / Exhibition", code: "EXHIB", category: 3, sortOrder: 20, isSystem: true, extendedInfoType: "OUT", color: "info", icon: MuseumIcon },
    { artworkStatusId: 22, name: "In Transit", code: "TRANS", category: 3, sortOrder: 30, isSystem: true, extendedInfoType: "OUT", color: "info", icon: LocalShippingIcon },
    { artworkStatusId: 23, name: "With Restorer", code: "REST", category: 3, sortOrder: 40, isSystem: true, extendedInfoType: "OUT", color: "warning", icon: HandymanIcon },
    { artworkStatusId: 24, name: "In Storage", code: "STOR", category: 3, sortOrder: 50, isSystem: true, extendedInfoType: "OUT", color: "default", icon: WarehouseIcon },

    { artworkStatusId: 30, name: "Missing", code: "MISS", category: 4, sortOrder: 10, isSystem: true, extendedInfoType: "NONE", color: "error", icon: HelpOutlineIcon },
    { artworkStatusId: 31, name: "Lost", code: "LOST", category: 4, sortOrder: 20, isSystem: true, extendedInfoType: "NONE", color: "error", icon: SearchOffIcon },
    { artworkStatusId: 32, name: "Stolen", code: "STOL", category: 4, sortOrder: 30, isSystem: true, extendedInfoType: "NONE", color: "error", icon: ReportProblemIcon },
    { artworkStatusId: 33, name: "Damaged", code: "DAMG", category: 4, sortOrder: 40, isSystem: true, extendedInfoType: "NONE", color: "error", icon: BrokenImageIcon },
    { artworkStatusId: 34, name: "Destroyed", code: "DEST", category: 4, sortOrder: 50, isSystem: true, extendedInfoType: "NONE", color: "error", icon: DeleteForeverIcon },

    { artworkStatusId: 35, name: "With Brokerage", code: "NA", category: 5, sortOrder: 0, isSystem: false, extendedInfoType: "NONE", color: "secondary", icon: AccountBalanceIcon },
    { artworkStatusId: 40, name: "With Gallery/Auction House", code: "WITH_GALLERY", category: 5, sortOrder: 0, isSystem: false, extendedInfoType: "NONE", color: "secondary", icon: StorefrontIcon },
];

export const artworkStatusById: Map<number, ArtworkStatusInfo> = new Map(
    ARTWORK_STATUSES.map((s) => [s.artworkStatusId, s])
);

export function getArtworkStatusInfo(statusId: number | null | undefined): ArtworkStatusInfo | undefined {
    if (statusId == null) return undefined;
    return artworkStatusById.get(statusId);
}